/* eslint-disable react/function-component-definition */
import styles from './RootCategoryTemplate.module.css'
import {
  DSPostShortWithAuthor,
  DSResource,
  DSRootCategoryPage,
  NCComponentInterface,
} from 'shared-definitions/types'
import styleUtils from 'shared-components/styles/util.module.css'
import clsx from 'clsx'
import ImagedLink from 'shared-components/ui/ImagedLink'
import RootCategoryHero from 'shared-components/ui/RootCategoryHero'
import { ComponentType, useCallback } from 'react'
import PostRow from 'shared-components/posts/PostRow'
import LoadMoreList from 'shared-components/ui/LoadMoreList'
import SidebarLayout from 'shared-components/ui/SidebarLayout'
import { useCategoryViewed } from '../hooks/use-category-viewed'
import ContentLayout from '../ui/ContentLayout'

interface RootCategoryTemplateProps<T extends DSResource> {
  page: DSRootCategoryPage<T>
  pageLoader: (page: number, perPage: number) => Promise<DSPostShortWithAuthor[]>
  sideBar: React.ReactElement
  leaderboard: React.ReactElement
  nc: ComponentType<NCComponentInterface<T>>
  incontentAds?: React.ReactNode[]
  generateIncontentAdPlacementIndex?: () => number
}

function RootCategoryTemplate<T extends DSResource>({
  page,
  pageLoader,
  nc: NC,
  sideBar,
  leaderboard,
  incontentAds,
  generateIncontentAdPlacementIndex,
}: RootCategoryTemplateProps<T>): React.ReactElement {
  const [hero1, hero2] = page.hero

  return (
    <div className={styleUtils.wrapper} ref={useCategoryViewed(page.title, page.pagePath, 1)}>
      {page.slots.top.length > 0 && (
        <div>
          {page.slots.top.map((item, index) => (
            <NC key={index} index={index} data={item} tpl="wide" />
          ))}
        </div>
      )}
      <h1 className={clsx(styleUtils.pageTitle, styles.titleMargins)}>{page.title}</h1>
      {page.topics ? (
        <div className={styles.categoriesGrid}>
          {page.topics.items.map((item, index) => (
            <ImagedLink key={index} {...item} />
          ))}
        </div>
      ) : null}
      {page.slots.content.length > 0 && (
        <ContentLayout className={clsx(styles.slotsContent)}>
          {page.slots.content.map((item, index) => (
            <NC key={index} index={index} data={item} tpl="wide" />
          ))}
        </ContentLayout>
      )}
      {hero1 ? <RootCategoryHero {...hero1} className={styles.hero} /> : null}
      {hero2 ? (
        <RootCategoryHero {...hero2} reversed className={clsx(styles.hero, styles.secondHero)} />
      ) : null}
      {leaderboard}
      <h2 className={styles.listTitle}>{page.latest.title}</h2>
      <SidebarLayout
        content={
          <LoadMoreList
            autoload
            render={useCallback(
              (item: DSPostShortWithAuthor, index) => (
                <>
                  <PostRow key={index} post={item} large />
                  {incontentAds &&
                    generateIncontentAdPlacementIndex &&
                    (index + 1) % 5 === 0 &&
                    incontentAds[generateIncontentAdPlacementIndex()]}
                </>
              ),
              []
            )}
            className={styles.list}
            initial={page.latest.posts}
            loader={pageLoader}
          />
        }
        sidebar={
          <div className={styles.sidebar}>
            {page.sidebarAds.map((item, index) => (
              <NC index={index} key={index} data={item} tpl="sidebar" />
            ))}
            {sideBar}
          </div>
        }
      />
    </div>
  )
}

export default RootCategoryTemplate
